import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";

/**
 * Task Types per spec 03-task-approval-workflow.md
 */
export type TaskType =
  | "po_receiving"
  | "receipt_approval"
  | "invoice_approval"
  | "payment_approval"
  | "return_review";

export type TaskStatus = "pending" | "in_progress" | "completed" | "returned";

export type TaskPriority = "low" | "normal" | "high" | "urgent";

export type TaskAction =
  | "created"
  | "assigned"
  | "approved"
  | "returned"
  | "delegated"
  | "completed";

export type EntityType = "po" | "invoice" | "payment" | "receipt";

/**
 * Task History Entry for audit trail (TW-05)
 */
export interface TaskHistoryEntry {
  id: string;
  action: TaskAction;
  performedBy: string;
  performedByName: string;
  performedAt: Date;
  notes: string | null;
  previousAssignee: string | null;
  previousAssigneeName: string | null;
  newAssignee: string | null;
  newAssigneeName: string | null;
}

/**
 * Workflow Task per spec (TW-01 through TW-10)
 */
export interface WorkflowTask {
  id: string;
  taskType: TaskType;
  entityType: EntityType;
  entityId: string;
  entityNumber: string; // e.g., PO-2024-001
  plantId: string;
  plantCode: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;

  // Assignment
  assignedTo: string;
  assignedToName: string;
  assignedAt: Date;
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  completedAt: Date | null;
  dueDate: Date | null;

  // Entity details for quick view
  vendorName?: string;
  totalAmount?: number;
  jobNumber?: string;

  // Documents attached
  hasQuote?: boolean;
  hasPackingSlip?: boolean;
  hasInvoice?: boolean;

  // History
  history: TaskHistoryEntry[];

  // Return info
  returnReason?: string;
  returnedBy?: string;
  returnedAt?: Date;
}

/**
 * Workflow Rule for configurable routing (TW-02)
 */
export interface WorkflowRule {
  id: string;
  taskType: TaskType;
  plantId: string | null; // null = all plants
  sequence: number;
  roleId: string | null;
  userId: string | null; // specific user override
  roleName?: string;
  userName?: string;
  isActive: boolean;
}

/**
 * User queue summary for manager overview (TW-08)
 */
export interface UserQueueSummary {
  userId: string;
  userName: string;
  role: string;
  pendingCount: number;
  overdueCount: number;
  oldestTaskAge: number; // days
  tasks: WorkflowTask[];
}

/**
 * Task Workflow Store
 *
 * Manages the digital task queue system per spec 03-task-approval-workflow.md
 * - Personal task bucket (TW-01)
 * - Configurable routing rules (TW-02)
 * - Approve/Return actions (TW-03, TW-04)
 * - Task history/audit trail (TW-05)
 * - Escalation rules (TW-06)
 * - Task delegation (TW-07)
 * - Manager overview (TW-08)
 * - SLA/aging indicators (TW-09)
 */
class TaskWorkflowStore {
  rootStore: RootStore;

  // Filters
  taskTypeFilter: TaskType | "all" = "all";
  statusFilter: TaskStatus | "all" = "pending";
  priorityFilter: TaskPriority | "all" = "all";
  plantFilter: string | null = null;
  searchTerm: string = "";
  assigneeFilter: string | "my_tasks" | "all" = "my_tasks";

  // Sort
  sortField: "assignedAt" | "dueDate" | "priority" | "totalAmount" = "assignedAt";
  sortDirection: "asc" | "desc" = "asc";

  // UI State
  error: string | null = null;
  selectedTask: WorkflowTask | null = null;
  returnNote: string = "";
  delegateToUserId: string = "";

  // Manager view
  showManagerView = false;
  userQueues: UserQueueSummary[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== ServiceStore Access ====================

  get approvalQueueServiceStore() {
    return this.rootStore.serviceStores?.["approval-queue"];
  }

  get workflowRulesServiceStore() {
    return this.rootStore.serviceStores?.["workflow-rules"];
  }

  get taskHistoryServiceStore() {
    return this.rootStore.serviceStores?.["task-history"];
  }

  // ==================== Computed: Data ====================

  get tasks(): WorkflowTask[] {
    return (this.approvalQueueServiceStore?.entities as WorkflowTask[]) || [];
  }

  get workflowRules(): WorkflowRule[] {
    return (this.workflowRulesServiceStore?.entities as WorkflowRule[]) || [];
  }

  get loading(): boolean {
    return this.approvalQueueServiceStore?.isFindPending || false;
  }

  // ==================== Computed Properties ====================

  /**
   * Get current user ID from auth store
   */
  get currentUserId(): string {
    const authStore = (this.rootStore as any).authStore;
    return authStore?.currentUser?.id || authStore?.currentUser?.userId || "";
  }

  /**
   * Get current user name from auth store
   */
  get currentUserName(): string {
    const authStore = (this.rootStore as any).authStore;
    const user = authStore?.currentUser;
    if (!user) return "";
    return user.name || `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username || "";
  }

  /**
   * Check if current user is a manager
   */
  get isManager(): boolean {
    const authStore = (this.rootStore as any).authStore;
    const role = authStore?.currentUser?.role;
    return role === "admin" || role === "manager" || role === "accounting_admin";
  }

  /**
   * Calculate task age in days
   */
  getTaskAge = (task: WorkflowTask): number => {
    const now = new Date();
    const assigned = new Date(task.assignedAt);
    const diffMs = now.getTime() - assigned.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };

  /**
   * Get aging status color per spec (TW-09)
   * Green: < 1 day
   * Yellow: 1-3 days
   * Red: > 3 days
   */
  getAgingStatus = (task: WorkflowTask): "success" | "warning" | "error" => {
    const age = this.getTaskAge(task);
    if (age < 1) return "success";
    if (age <= 3) return "warning";
    return "error";
  };

  /**
   * Get aging label
   */
  getAgingLabel = (task: WorkflowTask): string => {
    const age = this.getTaskAge(task);
    if (age === 0) return "Today";
    if (age === 1) return "1 day";
    return `${age} days`;
  };

  /**
   * Check if task is overdue (> 3 days) for escalation (TW-06)
   */
  isTaskOverdue = (task: WorkflowTask): boolean => {
    return this.getTaskAge(task) > 3;
  };

  /**
   * Filtered and sorted tasks for current view
   */
  get filteredTasks(): WorkflowTask[] {
    let filtered = [...this.tasks];

    // Assignee filter (TW-01 - personal bucket)
    if (this.assigneeFilter === "my_tasks") {
      filtered = filtered.filter((t) => t.assignedTo === this.currentUserId);
    } else if (this.assigneeFilter !== "all") {
      filtered = filtered.filter((t) => t.assignedTo === this.assigneeFilter);
    }

    // Task type filter
    if (this.taskTypeFilter !== "all") {
      filtered = filtered.filter((t) => t.taskType === this.taskTypeFilter);
    }

    // Status filter
    if (this.statusFilter !== "all") {
      filtered = filtered.filter((t) => t.status === this.statusFilter);
    }

    // Priority filter
    if (this.priorityFilter !== "all") {
      filtered = filtered.filter((t) => t.priority === this.priorityFilter);
    }

    // Plant filter
    if (this.plantFilter) {
      filtered = filtered.filter((t) => t.plantId === this.plantFilter);
    }

    // Search
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.title.toLowerCase().includes(search) ||
          t.entityNumber.toLowerCase().includes(search) ||
          t.vendorName?.toLowerCase().includes(search) ||
          t.description.toLowerCase().includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (this.sortField) {
        case "assignedAt":
          aVal = new Date(a.assignedAt).getTime();
          bVal = new Date(b.assignedAt).getTime();
          break;
        case "dueDate":
          aVal = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          bVal = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          break;
        case "priority":
          const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
          aVal = priorityOrder[a.priority];
          bVal = priorityOrder[b.priority];
          break;
        case "totalAmount":
          aVal = a.totalAmount || 0;
          bVal = b.totalAmount || 0;
          break;
        default:
          aVal = new Date(a.assignedAt).getTime();
          bVal = new Date(b.assignedAt).getTime();
      }

      if (this.sortDirection === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }

  /**
   * Summary counts for dashboard
   */
  get myTasksCount(): number {
    return this.tasks.filter(
      (t) => t.assignedTo === this.currentUserId && t.status === "pending"
    ).length;
  }

  get myUrgentCount(): number {
    return this.tasks.filter(
      (t) =>
        t.assignedTo === this.currentUserId &&
        t.status === "pending" &&
        t.priority === "urgent"
    ).length;
  }

  get myOverdueCount(): number {
    return this.tasks.filter(
      (t) =>
        t.assignedTo === this.currentUserId &&
        t.status === "pending" &&
        this.isTaskOverdue(t)
    ).length;
  }

  get completedTodayCount(): number {
    const today = new Date().toDateString();
    return this.tasks.filter(
      (t) =>
        t.assignedTo === this.currentUserId &&
        t.status === "completed" &&
        t.completedAt &&
        new Date(t.completedAt).toDateString() === today
    ).length;
  }

  /**
   * Tasks grouped by type for overview
   */
  get tasksByType(): Record<TaskType, WorkflowTask[]> {
    const grouped: Record<TaskType, WorkflowTask[]> = {
      po_receiving: [],
      receipt_approval: [],
      invoice_approval: [],
      payment_approval: [],
      return_review: [],
    };

    this.filteredTasks.forEach((task) => {
      grouped[task.taskType].push(task);
    });

    return grouped;
  }

  /**
   * Manager overview: all user queues (TW-08)
   */
  get allUserQueues(): UserQueueSummary[] {
    // Group tasks by assignee
    const byUser = new Map<string, WorkflowTask[]>();

    this.tasks
      .filter((t) => t.status === "pending")
      .forEach((task) => {
        const existing = byUser.get(task.assignedTo) || [];
        existing.push(task);
        byUser.set(task.assignedTo, existing);
      });

    const summaries: UserQueueSummary[] = [];

    byUser.forEach((tasks, userId) => {
      const overdueCount = tasks.filter((t) => this.isTaskOverdue(t)).length;
      const ages = tasks.map((t) => this.getTaskAge(t));
      const oldestAge = ages.length > 0 ? Math.max(...ages) : 0;

      summaries.push({
        userId,
        userName: tasks[0]?.assignedToName || "Unknown",
        role: this.getUserRole(userId),
        pendingCount: tasks.length,
        overdueCount,
        oldestTaskAge: oldestAge,
        tasks,
      });
    });

    // Sort by pending count descending
    return summaries.sort((a, b) => b.pendingCount - a.pendingCount);
  }

  // ==================== Filter Actions ====================

  setTaskTypeFilter = (type: TaskType | "all") => {
    this.taskTypeFilter = type;
  };

  setStatusFilter = (status: TaskStatus | "all") => {
    this.statusFilter = status;
  };

  setPriorityFilter = (priority: TaskPriority | "all") => {
    this.priorityFilter = priority;
  };

  setPlantFilter = (plantId: string | null) => {
    this.plantFilter = plantId;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setAssigneeFilter = (assignee: string | "my_tasks" | "all") => {
    this.assigneeFilter = assignee;
  };

  setSort = (field: "assignedAt" | "dueDate" | "priority" | "totalAmount") => {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
  };

  clearFilters = () => {
    this.taskTypeFilter = "all";
    this.statusFilter = "pending";
    this.priorityFilter = "all";
    this.plantFilter = null;
    this.searchTerm = "";
    this.assigneeFilter = "my_tasks";
  };

  toggleManagerView = () => {
    this.showManagerView = !this.showManagerView;
  };

  // ==================== Task Actions ====================

  /**
   * Select a task for detail view
   */
  selectTask = (taskId: string) => {
    this.selectedTask = this.tasks.find((t) => t.id === taskId) || null;
  };

  /**
   * Clear selected task
   */
  clearSelectedTask = () => {
    this.selectedTask = null;
    this.returnNote = "";
  };

  /**
   * Set return note for return action
   */
  setReturnNote = (note: string) => {
    this.returnNote = note;
  };

  /**
   * Approve task (TW-03) - routes to next person in workflow
   */
  approveTask = async (taskId: string) => {
    try {
      await this.approvalQueueServiceStore?.patch(taskId, {
        action: "approve",
      });
      runInAction(() => {
        this.selectedTask = null;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to approve task";
      });
    }
  };

  /**
   * Return task (TW-04) - sends back to previous assignee with note
   */
  returnTask = async (taskId: string) => {
    if (!this.returnNote.trim()) return;

    try {
      await this.approvalQueueServiceStore?.patch(taskId, {
        action: "return",
        returnReason: this.returnNote,
      });
      runInAction(() => {
        this.returnNote = "";
        this.selectedTask = null;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to return task";
      });
    }
  };

  /**
   * Delegate task to another user (TW-07)
   */
  delegateTask = async (taskId: string, toUserId: string, toUserName: string) => {
    try {
      await this.approvalQueueServiceStore?.patch(taskId, {
        action: "delegate",
        delegateToUserId: toUserId,
        delegateToUserName: toUserName,
      });
      runInAction(() => {
        this.selectedTask = null;
        this.delegateToUserId = "";
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to delegate task";
      });
    }
  };

  // ==================== Helper Methods ====================

  /**
   * Get user role (mock)
   */
  getUserRole = (userId: string): string => {
    const roles: Record<string, string> = {
      "user-001": "Owner/Approver",
      "user-002": "Receiving",
      "user-003": "AP Clerk",
      "user-004": "Sales",
    };
    return roles[userId] || "Staff";
  };

  /**
   * Get next assignee based on workflow rules
   */
  getNextAssignee = (
    task: WorkflowTask
  ): { userId: string; userName: string } | null => {
    // Mock workflow routing per spec diagram
    // Salesman → Jason (Receiving) → Clay (Approval) → Allie (AP)
    const routing: Record<TaskType, { userId: string; userName: string } | null> = {
      po_receiving: { userId: "user-001", userName: "Clay Anderson" },
      receipt_approval: { userId: "user-003", userName: "Allie Thompson" },
      invoice_approval: { userId: "user-003", userName: "Allie Thompson" },
      payment_approval: null, // Final step
      return_review: { userId: "user-002", userName: "Jason Miller" },
    };

    return routing[task.taskType];
  };

  /**
   * Get next task type after approval
   */
  getNextTaskType = (currentType: TaskType): TaskType => {
    const sequence: Record<TaskType, TaskType> = {
      po_receiving: "receipt_approval",
      receipt_approval: "payment_approval",
      invoice_approval: "payment_approval",
      payment_approval: "payment_approval",
      return_review: "po_receiving",
    };
    return sequence[currentType];
  };

  /**
   * Get task type label
   */
  getTaskTypeLabel = (type: TaskType): string => {
    const labels: Record<TaskType, string> = {
      po_receiving: "PO Receiving",
      receipt_approval: "Receipt Approval",
      invoice_approval: "Invoice Approval",
      payment_approval: "Payment Approval",
      return_review: "Return Review",
    };
    return labels[type];
  };

  // ==================== Dialog Actions ====================

  openTaskDetail = (taskId: string) => {
    this.selectTask(taskId);
    this.rootStore.routerStore.openDialog({
      dialogName: "taskDetail",
      dialogContext: taskId,
    });
  };

  openReturnDialog = (taskId: string) => {
    this.selectTask(taskId);
    this.rootStore.routerStore.openDialog({
      dialogName: "returnNote",
      dialogContext: taskId,
    });
  };

  openDelegateDialog = (taskId: string) => {
    this.selectTask(taskId);
    this.rootStore.routerStore.openDialog({
      dialogName: "delegate",
      dialogContext: taskId,
    });
  };

  openHistoryDialog = (taskId: string) => {
    this.selectTask(taskId);
    this.rootStore.routerStore.openDialog({
      dialogName: "taskHistory",
      dialogContext: taskId,
    });
  };

  // ==================== Data Loading ====================

  loadTasks = async () => {
    this.error = null;
    try {
      await this.approvalQueueServiceStore?.find({ query: { $limit: 500 } });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load tasks";
      });
    }
  };

  reset = () => {
    this.clearFilters();
    this.selectedTask = null;
    this.returnNote = "";
    this.delegateToUserId = "";
    this.showManagerView = false;
    this.error = null;
  };
}

export default TaskWorkflowStore;
